import {useAuthUserStore} from "@/stores/auth";

/*
 * Endpoints V2 del currículo de un curso (secciones y lecciones) definidos en
 * las tareas de backend LMS-060…065. `courseUuid` es el uuid del proyecto.
 */
const coursePath = (courseUuid) => '/v2/lms/courses/' + courseUuid
const sectionPath = (courseUuid, sectionUuid) => coursePath(courseUuid) + '/sections/' + sectionUuid
const lessonPath = (courseUuid, sectionUuid, lessonUuid) => sectionPath(courseUuid, sectionUuid) + '/lessons/' + lessonUuid

export default {
    //=============================================================
    //                          ORDEN
    //=============================================================
    // LMS-061: orden completo de las secciones del curso.
    reorderSections(courseUuid, sectionUuids) {
        return useAuthUserStore().getApiClient.put(coursePath(courseUuid) + '/sections/order', {
            ids: sectionUuids
        })
    },
    // LMS-063: orden de las lecciones de cada sección; permite mover lecciones entre secciones.
    syncCurriculum(courseUuid, curriculumOrder) {
        return useAuthUserStore().getApiClient.put(coursePath(courseUuid) + '/curriculum', {
            sections: curriculumOrder.map((section) => ({
                section_id: section.uuid,
                lesson_ids: section.lessons
            }))
        })
    },

    //=============================================================
    //                          SECTIONS
    //=============================================================
    // LMS-060: crea una sección al final del curso. Devuelve la sección (sin lecciones).
    createSection(courseUuid, title) {
        return useAuthUserStore().getApiClient.post(coursePath(courseUuid) + '/sections', {title})
    },
    // LMS-220: copia la sección con sus lecciones y bloques justo detrás, en borrador.
    // Devuelve la sección (con `lessons_count`, sin las lecciones).
    duplicateSection(courseUuid, sectionUuid) {
        return useAuthUserStore().getApiClient.post(sectionPath(courseUuid, sectionUuid) + '/duplicate')
    },
    // Lecciones de una sección (LMS-062), p. ej. las de una sección recién duplicada.
    sectionLessons(courseUuid, sectionUuid) {
        return useAuthUserStore().getApiClient.get(sectionPath(courseUuid, sectionUuid) + '/lessons')
    },
    // Con lecciones, el backend responde 409 salvo que se envíe `cascade=true`.
    deleteSection(courseUuid, sectionUuid, cascade = false) {
        return useAuthUserStore().getApiClient.delete(sectionPath(courseUuid, sectionUuid), {
            params: cascade ? {cascade: true} : {}
        })
    },

    // Actualización parcial de la sección (LMS-060). Con LMS-108/109 incluye la programación:
    // `drip_days_after_enrolment` o `available_from`, excluyentes.
    updateSection(courseUuid, sectionUuid, fields) {
        return useAuthUserStore().getApiClient.put(sectionPath(courseUuid, sectionUuid), fields)
    },

    // LMS-064: publicar (POST) o despublicar (DELETE) una sección.
    setSectionPublished(courseUuid, sectionUuid, published) {
        const apiClient = useAuthUserStore().getApiClient
        const url = sectionPath(courseUuid, sectionUuid) + '/publish'
        return published ? apiClient.post(url) : apiClient.delete(url)
    },

    //=============================================================
    //                          LESSONS
    //=============================================================
    // LMS-062 y LMS-219: crea una lección al final de la sección; el tipo ya no hace falta.
    createLesson(courseUuid, sectionUuid, title) {
        return useAuthUserStore().getApiClient.post(sectionPath(courseUuid, sectionUuid) + '/lessons', {title})
    },
    // LMS-064: publicar (POST) o despublicar (DELETE) una lección.
    setLessonPublished(courseUuid, sectionUuid, lessonUuid, published) {
        const apiClient = useAuthUserStore().getApiClient
        const url = lessonPath(courseUuid, sectionUuid, lessonUuid) + '/publish'
        return published ? apiClient.post(url) : apiClient.delete(url)
    },
    // Actualización parcial: solo se envían los campos que cambian (p. ej. `{ title }`).
    updateLesson(courseUuid, sectionUuid, lessonUuid, fields) {
        return useAuthUserStore().getApiClient.put(lessonPath(courseUuid, sectionUuid, lessonUuid), fields)
    },
    // LMS-220: copia la lección con sus bloques justo detrás, en borrador.
    duplicateLesson(courseUuid, sectionUuid, lessonUuid) {
        return useAuthUserStore().getApiClient.post(lessonPath(courseUuid, sectionUuid, lessonUuid) + '/duplicate')
    },
    deleteLesson(courseUuid, sectionUuid, lessonUuid) {
        return useAuthUserStore().getApiClient.delete(lessonPath(courseUuid, sectionUuid, lessonUuid))
    },
}
